import * as Sentry from '@sentry/react';
import type { DriverState } from '@playcastdotio/CoreTypes';
import type { DeviceConfigManager } from './DeviceConfigManager';

const DIAG = '[BLUE-STREAM-12010:ShadowcamPresenceMonitor]';

/**
 * Shadowcams that PlayJector starts at launch (always-on). These MUST be visible
 * to Chromium's `enumerateDevices()` at launch — Windows MediaFoundation only
 * exposes a virtual capture device after PlayJector starts it, so requiring an
 * on-demand cam here would make the host permanently `shadowcam_unhealthy`
 * until a corresponding peer connects.
 *
 * Mirrors DESKTOP_SHADOWCAM in PeerCoordinator.
 */
const LAUNCH_REQUIRED_SHADOWCAMS: readonly string[] = ['ShadowCam03'];

/**
 * Shadowcams PlayJector activates reactively on demand (e.g. ShadowCam01 is
 * started by PeerCoordinator when a mobile peer connects). Their kernel-level
 * driver must still be present (driverState reports them), but they will NOT
 * appear in `enumerateDevices()` until activated, so they are excluded from
 * the launch-time enumeration check.
 *
 * Mirrors MOBILE_SHADOWCAM in PeerCoordinator.
 */
const ON_DEMAND_SHADOWCAMS: readonly string[] = ['ShadowCam01'];

/** All shadowcams whose driver state the host cares about. */
const KNOWN_SHADOWCAM_NAMES: readonly string[] = [...LAUNCH_REQUIRED_SHADOWCAMS, ...ON_DEMAND_SHADOWCAMS];

/**
 * Grace window before we report "shadowcams missing at launch" to Sentry.
 *
 * The first `enumerateDevices()` always runs before host-electron's `getSources`
 * reply handler activates ShadowCam03 (~700 ms gap in real-world logs), so a
 * tight launch-time check would fire a false-positive on every boot. We wait
 * for the `devicechange` listener in DeviceConfigManager to drive a settled
 * health value before capturing.
 */
export const LAUNCH_PRESENCE_SETTLE_MS = 5_000;

export type ShadowcamUnhealthyReason = 'shadowcam_unhealthy';

export type ShadowcamHealth = {
  healthy: boolean;
  reason: ShadowcamUnhealthyReason | null;
  enumeratedShadowcams: number;
  driverShadowcams: { name: string; present: boolean }[] | null;
};

export type ShadowcamHealthListener = (health: ShadowcamHealth) => void;

export interface ShadowcamHealthSource {
  getHealth(): ShadowcamHealth;
  onHealthChange(listener: ShadowcamHealthListener): () => void;
}

type DriverStateLike = DriverState | Record<string, boolean> | undefined;
type GetDriverStateFn = () => DriverStateLike;

/**
 * Single source of truth for shadowcam health (BLUE-STREAM-12010).
 * Cross-references Chromium device enumeration with PlayJector driverState
 * so the matchmaker gate, offer-refusal gate, and Sentry issues never drift.
 */
export class ShadowcamPresenceMonitor {
  private readonly listeners = new Set<ShadowcamHealthListener>();
  private lastNotifiedHealth: ShadowcamHealth | null = null;
  private hasReportedDriverStatusIssue = false;
  private launchSettleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly deviceConfig: DeviceConfigManager,
    private readonly getDriverState: GetDriverStateFn,
    private readonly settleMs: number = LAUNCH_PRESENCE_SETTLE_MS,
  ) {}

  /**
   * Call once after MediaCoordinator's first enumeration.
   *
   * Always emits an info breadcrumb with the boot snapshot. Defers any
   * unhealthy Sentry capture by `settleMs` so the normal boot race (host-electron
   * activating ShadowCam03 ~700 ms after host-frontend boots) does NOT produce a
   * spurious issue on every launch. The deferred capture is cancelled if health
   * flips to healthy during the settle window via `refreshAfterEnumeration`.
   */
  public reportLaunchPresence(): void {
    const health = this.computeHealth();

    Sentry.addBreadcrumb({
      category: 'BLUE-STREAM-12010',
      level: 'info',
      message: 'Shadowcam presence at launch',
      data: {
        enumeratedShadowcams: health.enumeratedShadowcams,
        driverShadowcams: health.driverShadowcams,
      },
    });

    this.cancelLaunchSettleTimer();
    if (!health.healthy) {
      console.warn(
        `${DIAG} Shadowcams not present at first enumerate — waiting ${this.settleMs}ms for devicechange to settle`,
        health,
      );
      this.launchSettleTimer = setTimeout(() => {
        this.launchSettleTimer = null;
        const settled = this.computeHealth();
        if (settled.healthy) return;
        console.error(`${DIAG} Shadowcams still not present after settle window`, settled);
        Sentry.captureMessage('[BLUE-STREAM-12010] Shadowcams not present at host launch', {
          level: 'error',
          tags: {
            shadowcam_diag: 'BLUE-STREAM-12010',
            shadowcam_stage: 'launch_presence',
          },
          fingerprint: ['shadowcam-missing-at-launch'],
          extra: {
            enumeratedShadowcams: settled.enumeratedShadowcams,
            driverShadowcams: settled.driverShadowcams,
          },
        });
      }, this.settleMs);
    }

    this.notifyHealth(health);
  }

  private cancelLaunchSettleTimer(): void {
    if (this.launchSettleTimer !== null) {
      clearTimeout(this.launchSettleTimer);
      this.launchSettleTimer = null;
    }
  }

  /** Idempotent: fires the dead-driver issue at most once per session. */
  public reportDriverStatus(): void {
    const driverShadowcams = this.collectDriverShadowcams();

    if (!this.hasReportedDriverStatusIssue && driverShadowcams) {
      const dead = driverShadowcams.filter((d) => !d.present);
      if (dead.length > 0) {
        this.hasReportedDriverStatusIssue = true;
        console.error(`${DIAG} driverState reports dead shadowcams`, dead);
        Sentry.captureMessage('[BLUE-STREAM-12010] driverState reports dead shadowcams', {
          level: 'error',
          tags: {
            shadowcam_diag: 'BLUE-STREAM-12010',
            shadowcam_stage: 'driver_status_dead',
          },
          fingerprint: ['driver-status-reports-dead-shadowcams'],
          extra: { driverShadowcams, deadShadowcams: dead.map((d) => d.name) },
        });
      }
    }

    this.notifyHealth(this.computeHealth());
  }

  public refreshAfterEnumeration(): void {
    const health = this.computeHealth();
    if (health.healthy) {
      this.cancelLaunchSettleTimer();
    }
    this.notifyHealth(health);
  }

  public getHealth(): ShadowcamHealth {
    return this.computeHealth();
  }

  public onHealthChange(listener: ShadowcamHealthListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public destroy(): void {
    this.cancelLaunchSettleTimer();
    this.listeners.clear();
  }

  private computeHealth(): ShadowcamHealth {
    const enumerated = this.deviceConfig.getShadowcams();
    const driverShadowcams = this.collectDriverShadowcams();

    // Enumeration check covers only always-on cams. On-demand cams (e.g. ShadowCam01)
    // are not exposed by Windows MediaFoundation until PlayJector starts them, so
    // they cannot be required by the launch-time gate.
    const enumeratedHealthy = LAUNCH_REQUIRED_SHADOWCAMS.every((name) =>
      enumerated.some((device) => device.displayName.includes(name)),
    );
    const driverHealthy = !driverShadowcams || driverShadowcams.every((d) => d.present);

    const healthy = enumeratedHealthy && driverHealthy;
    return {
      healthy,
      reason: healthy ? null : 'shadowcam_unhealthy',
      enumeratedShadowcams: enumerated.length,
      driverShadowcams,
    };
  }

  private collectDriverShadowcams(): { name: string; present: boolean }[] | null {
    const drivers = this.getDriverState();
    if (!drivers || Object.keys(drivers).length === 0) {
      return null;
    }
    const record = drivers as Record<string, boolean>;
    return KNOWN_SHADOWCAM_NAMES.map((name) => ({
      name,
      present: record[name] === true,
    }));
  }

  private notifyHealth(health: ShadowcamHealth): void {
    if (this.healthEquals(this.lastNotifiedHealth, health)) {
      return;
    }
    this.lastNotifiedHealth = health;
    for (const listener of this.listeners) {
      try {
        listener(health);
      } catch (err) {
        console.error(`${DIAG} health listener threw`, err);
      }
    }
  }

  private healthEquals(a: ShadowcamHealth | null, b: ShadowcamHealth): boolean {
    if (!a) return false;
    return (
      a.healthy === b.healthy &&
      a.reason === b.reason &&
      a.enumeratedShadowcams === b.enumeratedShadowcams &&
      JSON.stringify(a.driverShadowcams) === JSON.stringify(b.driverShadowcams)
    );
  }
}
